import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, ChevronRight, Building2, Layers3, DoorClosed, MonitorSmartphone, Trash2, Grid3x3 } from "lucide-react";
import camApi from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";

// Inline "add child" row shared by every level.
function AddRow({ placeholder, onAdd }) {
  const [v, setV] = useState("");
  const submit = async () => { if (!v.trim()) return; await onAdd(v.trim()); setV(""); };
  return (
    <div className="flex gap-1.5 mt-1">
      <Input className="h-8 text-sm" placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
      <Button size="sm" variant="outline" className="h-8" onClick={submit}><Plus className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

export default function Locations() {
  const { tree, reloadLookups } = useCam();
  const [open, setOpen] = useState({});
  const [seatLab, setSeatLab] = useState(null);
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  const refresh = async () => { await reloadLookups(); };

  const add = async (seg, body, okMsg) => {
    try { await camApi.createLoc(seg, body); toast.success(okMsg); await refresh(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (seg, id) => {
    if (!window.confirm("Deactivate this location? Assets keep their history.")) return;
    try { await camApi.removeLoc(seg, id); toast.success("Removed"); await refresh(); }
    catch (e) { toast.error(e.message); }
  };

  const buildingsFor = (cid) => tree.buildings.filter((b) => b.campus_id === cid);
  const floorsFor = (bid) => tree.floors.filter((fl) => fl.building_id === bid);
  const roomsFor = (fid) => tree.rooms.filter((r) => r.floor_id === fid);
  const labsFor = (rid) => tree.labs.filter((l) => l.room_id === rid);

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 glass rounded-2xl p-4 space-y-2">
        <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-cobalt-500" /><div className="overline">Campus hierarchy</div></div>
        <p className="text-xs text-muted-foreground mb-3">Campus → Building → Floor → Room → Lab. Add levels as you go; assets live at any level.</p>

        {tree.campuses.map((c) => (
          <div key={c.id} className="rounded-xl border">
            <Row icon={Building2} label={c.name} open={open[`c${c.id}`]} onToggle={() => toggle(`c${c.id}`)} onDelete={() => remove("campuses", c.id)} />
            {open[`c${c.id}`] && (
              <div className="pl-6 pr-3 pb-3 space-y-1.5">
                {buildingsFor(c.id).map((b) => (
                  <div key={b.id} className="rounded-lg border bg-white/40">
                    <Row icon={Building2} small label={b.name} open={open[`b${b.id}`]} onToggle={() => toggle(`b${b.id}`)} onDelete={() => remove("buildings", b.id)} />
                    {open[`b${b.id}`] && (
                      <div className="pl-6 pr-3 pb-2 space-y-1.5">
                        {floorsFor(b.id).map((fl) => (
                          <div key={fl.id} className="rounded-lg border bg-white/40">
                            <Row icon={Layers3} small label={fl.name} open={open[`f${fl.id}`]} onToggle={() => toggle(`f${fl.id}`)} onDelete={() => remove("floors", fl.id)} />
                            {open[`f${fl.id}`] && (
                              <div className="pl-6 pr-3 pb-2 space-y-1.5">
                                {roomsFor(fl.id).map((r) => (
                                  <div key={r.id} className="rounded-lg border bg-white/40">
                                    <Row icon={DoorClosed} small label={`${r.name}${r.room_type && r.room_type !== "room" ? ` · ${r.room_type}` : ""}`} open={open[`r${r.id}`]} onToggle={() => toggle(`r${r.id}`)} onDelete={() => remove("rooms", r.id)} />
                                    {open[`r${r.id}`] && (
                                      <div className="pl-6 pr-3 pb-2 space-y-1">
                                        {labsFor(r.id).map((l) => (
                                          <div key={l.id} className="flex items-center gap-2 py-1 text-sm">
                                            <MonitorSmartphone className="h-3.5 w-3.5 text-muted-foreground" />
                                            <span className="flex-1">{l.name}</span>
                                            {l.rows > 0 && <span className="text-[11px] text-muted-foreground">{l.rows}×{l.cols}</span>}
                                            <button title="Seats" onClick={() => setSeatLab(l)} className="text-muted-foreground hover:text-cobalt-500 p-0.5"><Grid3x3 className="h-3.5 w-3.5" /></button>
                                            <button onClick={() => remove("labs", l.id)} className="text-muted-foreground hover:text-red-500 p-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
                                          </div>
                                        ))}
                                        <AddRow placeholder="Add lab…" onAdd={(name) => add("labs", { room_id: r.id, name }, "Lab added")} />
                                      </div>
                                    )}
                                  </div>
                                ))}
                                <AddRow placeholder="Add room…" onAdd={(name) => add("rooms", { floor_id: fl.id, name }, "Room added")} />
                              </div>
                            )}
                          </div>
                        ))}
                        <AddRow placeholder="Add floor…" onAdd={(name) => add("floors", { building_id: b.id, name }, "Floor added")} />
                      </div>
                    )}
                  </div>
                ))}
                <AddRow placeholder="Add building…" onAdd={(name) => add("buildings", { campus_id: c.id, name }, "Building added")} />
              </div>
            )}
          </div>
        ))}
        <AddRow placeholder="Add campus…" onAdd={(name) => add("campuses", { name }, "Campus added")} />
      </div>

      <div className="glass rounded-2xl p-4 space-y-2 self-start">
        <div className="flex items-center gap-2 mb-1"><Layers3 className="h-4 w-4 text-green-600" /><div className="overline">Departments</div></div>
        <p className="text-xs text-muted-foreground mb-2">CAM-scoped departments for asset ownership (EC, Civil, Mechanical…).</p>
        <div className="space-y-1">
          {tree.departments.map((d) => (
            <div key={d.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-black/[0.03] text-sm">
              <span className="flex-1">{d.name}</span>
              <button onClick={() => remove("departments", d.id)} className="text-muted-foreground hover:text-red-500 p-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {!tree.departments.length && <p className="text-xs text-muted-foreground py-2">No departments yet.</p>}
        </div>
        <AddRow placeholder="Add department…" onAdd={(name) => add("departments", { name }, "Department added")} />
      </div>

      {seatLab && <SeatDialog lab={seatLab} onClose={() => setSeatLab(null)} onDone={refresh} />}
    </div>
  );
}

function Row({ icon: Icon, label, small, open, onToggle, onDelete }) {
  return (
    <div className={`flex items-center gap-2 ${small ? "px-2.5 py-1.5" : "px-3 py-2.5"}`}>
      <button onClick={onToggle} className="flex items-center gap-2 flex-1 text-left">
        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
        <Icon className={`${small ? "h-3.5 w-3.5" : "h-4 w-4"} text-cobalt-500`} />
        <span className={small ? "text-sm" : "font-medium text-sm"}>{label}</span>
      </button>
      <button onClick={onDelete} className="text-muted-foreground hover:text-red-500 p-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function SeatDialog({ lab, onClose, onDone }) {
  const [rows, setRows] = useState(lab.rows || 5);
  const [cols, setCols] = useState(lab.cols || 8);
  const [busy, setBusy] = useState(false);
  const [seats, setSeats] = useState(null);
  useEffect(() => { camApi.seats(lab.id).then((r) => setSeats(r.seats || [])).catch(() => setSeats([])); }, [lab.id]);
  const gen = async () => {
    setBusy(true);
    try { const r = await camApi.genSeats(lab.id, { rows: Number(rows), cols: Number(cols) }); toast.success(`${r.generated} seats generated`); const s = await camApi.seats(lab.id); setSeats(s.seats || []); onDone?.(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-background rounded-2xl p-5 w-full max-w-md">
        <div className="font-display text-lg tracking-tighter mb-1">Seat layout — {lab.name}</div>
        <p className="text-xs text-muted-foreground mb-3">Generate a grid of numbered seats (A1, A2…). Regenerating replaces the current layout.</p>
        <div className="flex items-end gap-2 mb-3">
          <div><label className="text-xs">Rows</label><Input type="number" min={1} max={60} value={rows} onChange={(e) => setRows(e.target.value)} className="h-9 mt-1 w-20" /></div>
          <div><label className="text-xs">Cols</label><Input type="number" min={1} max={60} value={cols} onChange={(e) => setCols(e.target.value)} className="h-9 mt-1 w-20" /></div>
          <Button onClick={gen} disabled={busy} className="ml-auto">{busy ? "…" : "Generate"}</Button>
        </div>
        {seats && seats.length > 0 && <p className="text-xs text-muted-foreground">{seats.length} seats: {seats.slice(0, 6).map((s) => s.label).join(", ")}{seats.length > 6 ? "…" : ""}</p>}
        <div className="flex justify-end mt-4"><Button variant="ghost" onClick={onClose}>Close</Button></div>
      </div>
    </div>
  );
}
